import React, { useState, useContext } from 'react';
import { AuthContext } from '../contexts/auth';
import { BriefcaseIcon, TrashIcon, CheckIcon, ClockIcon } from '@heroicons/react/24/outline';
import { PlusIcon } from '@heroicons/react/20/solid';
import { createTimeSlotCompanyCompound } from '../services/api_working_hours';
import { validateBreak, validateBreaks, validateTimeSlots } from '../utils/timeslots';
import Swal from 'sweetalert2';
import LocalBarOutlinedIcon from '@mui/icons-material/LocalBarOutlined';



const AddNewTimeSlot = ({ onCancelEdit, workingHour, onAddTimeSlot, allowHiddenBreaks }) => {
  const { token } = useContext(AuthContext);
  const [workStartTime, setWorkStartTime] = useState('');
  const [workEndTime, setWorkEndTime] = useState('');
  const [error, setError] = useState('');
  const [pauseTimes, setPauseTimes] = useState([]);
  const [creatingPause, setCreatingPause] = useState(false);
  const [workTimeError, setWorkTimeError] = useState('');
  const [breakTimeError, setBreakTimeError] = useState('');
  const [newTimeSlots, setNewTimeSlots] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  const handleCancelClick = () => {
      onCancelEdit();
  };

  const handleWorkStartTimeChange = (e) => {
    let inputValue = e.target.value;
    inputValue = inputValue.replace(/\D/g, '');
    inputValue = inputValue.slice(0, 4);

    let formattedTime = inputValue.replace(/(\d{2})(\d{0,2})/, "$1:$2");
  
    if (inputValue.length <= 2 && e.target.value.length < formattedTime.length) {
      formattedTime = formattedTime.replace(":", "");
    }
  
    setWorkStartTime(formattedTime);

    if (workEndTime && formattedTime >= workEndTime) {
      setWorkTimeError("L'orario di inizio deve essere precedente all'orario di fine.");
      return;
    } else {
      setWorkTimeError('');
      setError('');
    }
  };

  const handleWorkEndTimeChange = (e) => {
    let inputValue = e.target.value;
  
    inputValue = inputValue.replace(/\D/g, '');
    inputValue = inputValue.slice(0, 4);
  
    let formattedTime = inputValue.replace(/(\d{2})(\d{0,2})/, "$1:$2");
  
    if (inputValue.length <= 2 && e.target.value.length < formattedTime.length) {
      formattedTime = formattedTime.replace(":", "");
    }

    const [rawHours, rawMinutes = "0"] = formattedTime.split(":");
    const hours = parseInt(rawHours, 10);
    const minutes = parseInt(rawMinutes, 10);

    if (!isNaN(hours)) {
      if (hours > 24 || (hours === 24 && minutes > 0)) {
        setWorkTimeError("L'orario massimo consentito è 24:00.");
        return;
      }
    }

    setWorkEndTime(formattedTime);

    if (workStartTime && formattedTime <= workStartTime) {
      setWorkTimeError("L'orario di fine deve essere maggiore dell'orario di inizio");
      return;
    } else {
      setWorkTimeError('');
      setError('');
    }
  };

  const handleWorkTimeBlur = (field) => {
    let formattedTime = field === 'start' ? workStartTime : workEndTime;

    formattedTime = formattedTime.replace(/\D/g, '');

    while (formattedTime.length < 4) {
      formattedTime += '0';
    }

    formattedTime = formattedTime.slice(0, 4);

    const hours = formattedTime.slice(0, 2);
    const minutes = formattedTime.slice(2);
    formattedTime = `${hours}:${minutes}`;

    const isValidTime =
      /^([01]?[0-9]|2[0-4]):[0-5][0-9]$/.test(formattedTime) &&
      !(hours === '24' && minutes !== '00')

    if (!isValidTime) {
      if (field === 'start') {
        setWorkStartTime('');
      } else {
        setWorkEndTime('');
      }
      setWorkTimeError("L'orario massimo consentito è 24:00.");
      return;
    }

    setWorkTimeError('');

    if (field === 'start') {
      setWorkStartTime(formattedTime);
    } else {
      setWorkEndTime(formattedTime);
    }
  };
  
  
  const handleAddPause = () => {
    if (!workStartTime || !workEndTime) {
      setWorkTimeError("Prima di aggiungere una pausa, inserisci l'orario di lavoro.");
      return;
    }
  
    if (!creatingPause && pauseTimes.every(pause => pause.confirmed)) {
      setCreatingPause(true);
      setPauseTimes([...pauseTimes, { start_at: '', end_at: '', is_visible: true, confirmed: false }]);
      setWorkTimeError('');
    } else {
      setCreatingPause(false);
      setPauseTimes(pauseTimes.filter(pause => pause.confirmed));
      setBreakTimeError('');
    }
  };

  const updatePause = (index, patch) => {
    setPauseTimes(prev => prev.map((p, i) => i === index ? { ...p, ...patch } : p));
  };

  const handlePauseStartTimeChange = (index, e) => {
    const updatedPauseTimes = [...pauseTimes];
    let inputValue = e.target.value;
    
    inputValue = inputValue.replace(/\D/g, '');
    inputValue = inputValue.slice(0, 4);
    
    let formattedTime = inputValue.replace(/(\d{2})(\d{0,2})/, "$1:$2");
  
    if (inputValue.length <= 2 && e.target.value.length < formattedTime.length) {
      formattedTime = formattedTime.replace(":", "");
    }
    
    updatedPauseTimes[index].start_at = formattedTime;
    setPauseTimes(updatedPauseTimes);
  };
  
  const handlePauseEndTimeChange = (index, e) => {
    const updatedPauseTimes = [...pauseTimes];
    let inputValue = e.target.value;
    
    inputValue = inputValue.replace(/\D/g, '');
    inputValue = inputValue.slice(0, 4);
    
    let formattedTime = inputValue.replace(/(\d{2})(\d{0,2})/, "$1:$2");
  
    if (inputValue.length <= 2 && e.target.value.length < formattedTime.length) {
      formattedTime = formattedTime.replace(":", "");
    }
    updatedPauseTimes[index].end_at = formattedTime;
    setPauseTimes(updatedPauseTimes);
  };

const handlePauseTimeBlur = (index, field) => {
  const updatedPauseTimes = [...pauseTimes];
  let formattedTime = updatedPauseTimes[index][field];

  if (typeof formattedTime === 'string') {
    formattedTime = formattedTime.replace(/\D/g, '');

    while (formattedTime.length < 4) {
      formattedTime += '0';
    }

    formattedTime = formattedTime.slice(0, 4);

    const hours = formattedTime.slice(0, 2);
    const minutes = formattedTime.slice(2);
    formattedTime = `${hours}:${minutes}`;

    const isValidTime =
      /^([01]?[0-9]|2[0-4]):[0-5][0-9]$/.test(formattedTime) &&
      !(hours === '24' && minutes !== '00');

    if (!isValidTime) {
      updatedPauseTimes[index][field] = '';
      setPauseTimes(updatedPauseTimes);
      return;
    }

    updatedPauseTimes[index][field] = formattedTime;
    setPauseTimes(updatedPauseTimes);
  }
};

  const handleConfirmPause = (index) => {
    const updatedPauseTimes = [...pauseTimes];
    const currentPause = updatedPauseTimes[index];
    
    try {
      validateBreak(workStartTime, workEndTime, currentPause, pauseTimes);
      currentPause.confirmed = true;
      setPauseTimes(updatedPauseTimes);
      setCreatingPause(false);
      setBreakTimeError('');
    }
    catch(error) {
      console.log(error)
      setBreakTimeError(error.message);
      return;
    }
  };

  const handleDeletePause = (index) => {
    const updatedPauseTimes = [...pauseTimes];
    updatedPauseTimes.splice(index, 1);
    setPauseTimes(updatedPauseTimes);
  };


  const handleSaveTimeSlot = async () => {
    if (workTimeError || error) {
      return;
    }

    if (!workStartTime || !workEndTime) {
      setError("Prima di confermare, inserisci l'orario di lavoro.");
      return;
    }

    setError('');
    setIsSaving(true);
    
    try {
      const breaks = pauseTimes.map(breakItem => ({
        id: breakItem.id,
        start_at: breakItem.start_at,
        end_at: breakItem.end_at,
        is_visible: breakItem.is_visible ?? true,
      }));
  
      const workingHourId = workingHour?.id;
      setError('');

      const data = {
        content_type: 'string',
        start_at: workStartTime,
        end_at: workEndTime,
        breaks: breaks,
        object_id: workingHourId,
      };

      try {     
        // TODO CHIARA guarda qui!!!!! daiiii daiiii daiiii  
        validateBreaks(workStartTime, workEndTime, pauseTimes);
        validateTimeSlots(workStartTime, workEndTime,  workingHour.time_slots);       
      }
      catch (error) {
        setError(error.message);
        setIsSaving(false);
        return;
      }
      
      const response = await createTimeSlotCompanyCompound(token, data, 'workinghour', workingHourId);
      
      const updatedTimeSlots = [...workingHour.time_slots, response]; 

      setNewTimeSlots(updatedTimeSlots);
            
      if (onAddTimeSlot) {
        onAddTimeSlot(response);
        setError('');
      }
      
      setWorkStartTime('');
      setWorkEndTime('');
      setPauseTimes([]);
      setCreatingPause(false);
  
      handleCancelClick();
      Swal.fire({
        position: "center",
        icon: "success",
        title: "Salvato!",
        showConfirmButton: false,
        timer: 1500
      });
    } catch (error) {
      console.error('Errore durante la creazione del time slot:', error);
  
      let errorMessage = 'Errore durante la creazione della fascia oraria. Per favore riprova.';

      if (!error.response) {
        errorMessage = 'Errore, riprova più tardi.';
      } else if (error.response && error.response.data) {
        const errorData = error.response.data;
        const errorMessages = [];

        Object.keys(errorData).forEach((key) => {
          const messageArray = errorData[key];
          if (Array.isArray(messageArray)) {
            errorMessages.push(...messageArray);
          } else if (typeof messageArray === 'string') {
            errorMessages.push(messageArray);
          }
        });

        if (errorMessages.length > 0) {
          errorMessage = errorMessages.join(' ');
        }
      }
  
      Swal.fire({
        icon: 'error',
        title: 'Errore',
        text: errorMessage,
        confirmButtonText: 'OK',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      {(
        <div className='border border-solid-2 border-gray-200 shadow-md rounded-md p-4 '>
          <p className='text-xs font-medium text-gray-700 pt-2 m-0 col-span-1 flex items-center'>L'orario disponibile per la giornata è dalle 00:00 alle 24:00</p>
          <div className='grid grid-cols-1 mt-4'>
            {error && (
            <div className="grid grid-cols-1">
              <p className="text-red-500 text-sm text-center">{error}</p>
            </div>
            )}
            <div className='grid md:grid-cols-6 sm:grid-cols-1 gap-3 mt-4 justify-between'>
              <div className='md:col-span-3 sm:col-span-1'>
                <div className="mb-3 flex items-center">
                  <label htmlFor="start-time" className="w-32 text-sm font-medium text-gray-700 flex items-center space-x-1">
                    <BriefcaseIcon className='h-5 w-5 mr-2' />
                    Dalle ore:
                  </label>
                  <div style={{ position: 'relative', display: 'inline-block', width: 'fit-content' }}>
                    <input 
                      type="text"
                      placeholder='hh:mm' 
                      value={workStartTime} 
                      onInput={handleWorkStartTimeChange}
                      onBlur={() => handleWorkTimeBlur('start')}
                      className="mt-1 p-1 pl-4 border-gray-200 rounded-md focus:ring-indigo-500 focus:border-indigo-500 block w-32 shadow-md sm:text-sm required" 
                      style={{ paddingRight: '2.5rem' }}
                    />
                    <span style={{ position: 'absolute', top: '50%', right: '8px', transform: 'translateY(-50%)' }}>
                      <ClockIcon className='w-4 h-4' />
                    </span>
                  </div>
                </div>
              </div>
          
              <div className='md:col-span-3 sm:col-span-1 flex items-center'>
                <label htmlFor="start-time" className="w-32 text-sm font-medium text-gray-700 flex items-center space-x-1">
                  <BriefcaseIcon className='h-5 w-5 mr-2' />
                  Alle ore:
                </label>
                <div style={{ position: 'relative', display: 'inline-block', width: 'fit-content' }}>
                  <input 
                    type="text"
                    placeholder='hh:mm' 
                    value={workEndTime} 
                    onInput={handleWorkEndTimeChange} 
                    onBlur={() => handleWorkTimeBlur('end')}
                    className="t-1 p-1 pl-4 border-gray-200 rounded-md focus:ring-indigo-500 focus:border-indigo-500 block w-32 shadow-md sm:text-sm required" 
                  />
                  <span style={{ position: 'absolute', top: '50%', right: '8px', transform: 'translateY(-50%)' }}>
                    <ClockIcon className='w-4 h-4' />
                  </span>
                </div>
              </div>
            </div>
          </div>

          {workTimeError && (
            <p className="text-red-500 text-sm justify-center flex pt-8 ">{workTimeError}</p>
          )}

          {breakTimeError && (
            <p className="text-red-500 text-sm justify-center flex pt-8 ">{breakTimeError}</p>
          )}

          <div className='grid md:grid-cols-6 sm:grid-cols-1 pt-7 items-center'>
            <div className='col-span-6 flex items-center'>
              <label htmlFor="parent" className="block text-sm font-medium text-gray-700 m-0 col-span-1">Inserisci Fascia Oraria della Pausa</label>
              {!creatingPause && !pauseTimes.some(pause => !pause.confirmed) ? (
                <button onClick={handleAddPause} className="ml-3 border rounded-md bg-white py-1 px-2 mr-2 text-center text-sm font-semibold text-gray-700 shadow-md hover:bg-gray-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500 flex items-center">
                  <PlusIcon className='w-5 h-5 text-indigo-500 mr-1' />  
                  AGGIUNGI PAUSA
                </button>
              ) : (
                <button onClick={() => { setCreatingPause(false); setPauseTimes(pauseTimes.filter(pause => pause.confirmed)), setBreakTimeError('') }} className="ml-3 border rounded-md bg-white py-1 px-2 mr-2 text-center text-sm font-semibold text-gray-700 shadow-md hover:bg-gray-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500 flex items-center">
                  ANNULLA
                </button>
              )}
            </div>
          </div>
          

          {pauseTimes.map((pause, index) => {
            const hasNext = index < pauseTimes.length - 1;
            const hasWorkTimes = Boolean(workStartTime && workEndTime);
            const isEditingRow = !pause.confirmed;
            const disableTimeInputs = !hasWorkTimes || !isEditingRow || isSaving;
            const disableVisibility = isSaving;

            return (
              <div key={index} className="mt-4 w-full">
                <div className="grid grid-cols-12">

                  {/* START */}
                  <div className="col-span-6 sm:col-span-4 flex items-center justify-start">
                    <span className="text-sm font-medium text-gray-700 flex items-center pr-4">
                      <LocalBarOutlinedIcon className="!h-[17px] !w-[17px] mr-1" />
                      Dalle ore:
                    </span>

                    <div className="relative inline-block">
                      {!pause.confirmed ? (
                        <input
                          type="text"
                          placeholder="hh:mm"
                          value={pause.start_at}
                          onInput={(e) => handlePauseStartTimeChange(index, e)}
                          onBlur={() => handlePauseTimeBlur(index, "start_at")}
                          className="mt-1 p-1 pl-4 border-gray-200 rounded-md focus:border-indigo-500 block w-24 shadow-md sm:text-sm"
                          disabled={disableTimeInputs}
                        />
                      ) : (
                        <span className="mt-1 p-1 pl-4 rounded-md text-left block w-24 shadow-md sm:text-sm">
                          {pause.start_at}
                        </span>
                      )}

                      <ClockIcon className="w-4 h-4 absolute top-1/2 right-2 -translate-y-1/2" />
                    </div>
                  </div>

                  {/* END */}
                  <div className="col-span-6 sm:col-span-4 flex items-center justify-start">
                    <span className="text-sm font-medium text-gray-700 flex items-center pr-4">
                      <LocalBarOutlinedIcon className="!h-[17px] !w-[17px] mr-1" />
                      Alle ore:
                    </span>

                    <div className="relative inline-block">
                      {!pause.confirmed ? (
                        <input
                          type="text"
                          placeholder="hh:mm"
                          value={pause.end_at}
                          onInput={(e) => handlePauseEndTimeChange(index, e)}
                          onBlur={() => handlePauseTimeBlur(index, "end_at")}
                          className="mt-1 p-1 pl-4 border-gray-200 rounded-md focus:ring-indigo-500 focus:border-indigo-500 block w-24 shadow-md sm:text-sm"
                          disabled={disableTimeInputs}
                        />
                      ) : (
                        <span className="mt-1 p-1 pl-4 text-left rounded-md block w-24 shadow-md sm:text-sm">
                          {pause.end_at}
                        </span>
                      )}

                      <ClockIcon className="w-4 h-4 absolute top-1/2 right-2 -translate-y-1/2" />
                    </div>
                  </div>

                  {/* ACTIONS */}
                  <div className="col-span-12 sm:col-span-4 flex items-center justify-center gap-3 pt-3 flex-wrap">
                    {allowHiddenBreaks && (
                      <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          checked={!!(pause.is_visible ?? true)}
                          onChange={(e) =>
                            updatePause(index, { is_visible: e.target.checked })
                          }
                          disabled={disableVisibility}
                        />
                        <span className="text-xs font-medium uppercase">Visibile</span>
                      </label>
                    )}

                    {!pause.confirmed ? (
                      <button
                        onClick={() => handleConfirmPause(index)}
                        className="bg-indigo-600 text-white text-xs px-3 py-1 rounded-md hover:bg-indigo-500 font-semibold uppercase disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                        disabled={disableTimeInputs || !pause.start_at || !pause.end_at}
                      >
                        Aggiungi
                      </button>
                    ) : (
                      <button
                        onClick={() => handleDeletePause(index)}
                        className="text-gray-700 px-1"
                        title="Elimina"
                      >
                        <TrashIcon className="h-6 w-6 hover:text-red-500" />
                      </button>
                    )}
                  </div>
                </div>

                {/* DIVIDER SOLO MOBILE */}
                {hasNext && (
                  <div className="block sm:hidden mt-4">
                    <div className="border-b border-gray-200 mx-4"></div>
                  </div>
                )}
              </div>
            );
          })}
          <div className='grid grid-cols-2 gap-2 mt-7 mb-4'>
            <div className='col-span-1 flex justify-end'>
              <button 
                onClick={handleSaveTimeSlot} 
                className="bg-indigo-600 text-white text-sm px-4 py-2 rounded-md hover:bg-indigo-500 focus:outline-none focus:bg-indigo-600 w-24 font-semibold uppercase  focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500"
                disabled={isSaving}
                >
                    {isSaving ? 'Salvataggio...' : 'Salva'}
                </button>
            </div>

            <div className="col-span-1 flex justify-start">
              <button onClick={handleCancelClick} className="bg-gray-300 text-gray-700 text-sm px-4 py-2 rounded-md ml-4 hover:bg-gray-400 focus:outline-none focus:bg-gray-200 w-24 font-semibold uppercase focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500">Annulla</button>
            </div>

          </div>
        </div>
      )}
    </>
  );
}

export default AddNewTimeSlot;
